import { useQuery } from '@tanstack/react-query';
import { Select, TreeSelect } from 'antd';
import { FormikContextType, FormikContext } from 'formik';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Row, Col, FormGroup, FormLabel } from 'react-bootstrap';
import { ErrorMessageLabel } from '../../../components/ErrorMessageLabel/ErrorMessageLabel';
import { InputField } from '../../../components/InputField/InputField';
import { StoreProductValues } from '../CreateProductForm';
import { MdLock, MdLockOpen } from 'react-icons/md';
import { Textarea } from '../../../components/Textarea/Textarea';
import { InputWithSuffix } from '../../../components/InputWithSufix/InputWithSufix';
import { Category } from '../../../../models/Category/Category';
import { Product } from '../../../../models/Product/Product';
import { StorageRoom } from '../../../../models/Storage/StorageRoom';

interface StepOneProps {
  product?: Product;
}

function ProductInfo({ product }: StepOneProps) {
  const { values, setValues, setFieldValue } =
    useContext<FormikContextType<StoreProductValues>>(FormikContext);

  const { data: categories = [] } = useQuery(['categories'], () =>
    Category.all('attributes', 'features')
  );
  const [isPriceLocked, setIsPriceLocked] = useState(true);
  const [isPenaltyLocked, setIsPenaltyLocked] = useState(true);
  const categoryMap = useMemo(() => Category.toMap(categories), [categories]);

  const { data: rooms = [] } = useQuery(['storage-rooms'], StorageRoom.all);
  const [selectedRoom, setSelectedRoom] = useState<number | undefined>(
    product?.storageStand?.storageRoomId ?? undefined
  );
  useEffect(() => {
    if (!product && rooms.length > 0 && !selectedRoom) {
      setSelectedRoom(rooms[0].id);
      setFieldValue('storage_room_stand_id', undefined);
    }
  }, [rooms, product, selectedRoom, setFieldValue]);

  // Find all stands for selected room
  const standsForRoom =
    rooms.find((room) => room.id === selectedRoom)?.stands ?? [];

  // Auto-fill related fields when selecting category
  const setCategory = (category: number) => {
    const categoryObj = categoryMap.get(category) as Category;
    const { attributes, features, price, penaltyPrice } = categoryObj ?? {};

    setValues(
      {
        ...values,
        category,
        price: price ?? 0,
        penalty_price: penaltyPrice ?? 0
      },
      true
    );
    setIsPriceLocked(true);
    setIsPenaltyLocked(true);
  };

  return (
    <Row className="mt-5">
      {/* LEFT COLUMN: Product Basics */}
      <Col sm={6} className="cs-form-wrapper">
        <FormGroup>
          <FormLabel>Izaberi prostoriju</FormLabel>
          <Select
            value={selectedRoom}
            onChange={(roomId) => {
              setSelectedRoom(roomId);
              setFieldValue('storage_room_stand_id', undefined);
            }}
            style={{ width: '100%' }}
            placeholder="Izaberi prostoriju"
          >
            {rooms.map((room) => (
              <Select.Option key={room.id} value={room.id}>
                {room.name}
              </Select.Option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <FormLabel>Izaberi policu</FormLabel>
          <Select
            value={values.storage_room_stand_id}
            onChange={(standId) => setFieldValue('storage_room_stand_id', standId)}
            style={{ width: '100%' }}
            placeholder="Izaberi policu"
            disabled={!selectedRoom}
          >
            {standsForRoom.map((stand) => (
              <Select.Option key={stand.id} value={stand.id}>
                {stand.name}
              </Select.Option>
            ))}
          </Select>
          {/* Add this line for validation message */}
          <ErrorMessageLabel name="storage_room_stand_id" />
        </FormGroup>
        <FormGroup>
          <FormLabel>Ime proizvoda</FormLabel>
          <InputField name="name" autoComplete="off" />
          <ErrorMessageLabel name="name" />
        </FormGroup>
        <FormGroup>
          <FormLabel>Sku</FormLabel>
          <InputField name="sku" autoComplete="off" />
          <ErrorMessageLabel name="sku" />
        </FormGroup>
        <FormGroup>
          <FormLabel>Kratak opis</FormLabel>
          <Textarea name="short_description" rows={3} autoComplete="off" />
        </FormGroup>
      </Col>
      {/* RIGHT COLUMN: Room, Stand, Category */}
      <Col sm={6} className="cs-form-wrapper">
        <FormGroup>
          <FormLabel>Izaberi Kategoriju</FormLabel>
          <TreeSelect
            dropdownStyle={{ maxHeight: 500, overflow: 'auto' }}
            showSearch
            treeNodeFilterProp="title"
            treeDefaultExpandAll
            placeholder="Izaberi kategoriju"
            treeData={categories ?? []}
            value={values.category}
            onChange={(value: number) => {
              setCategory(value);
            }}
            className="cs-tree-select"
          />
          <ErrorMessageLabel name="category" />
        </FormGroup>
        <FormGroup>
          <FormLabel>Cena (RSD)</FormLabel>
          <InputWithSuffix
            name="price"
            type="number"
            step={0.1}
            autoComplete="off"
            disabled={isPriceLocked}
            suffix={isPriceLocked ? <MdLock /> : <MdLockOpen />}
            onSuffixClick={() => setIsPriceLocked((v) => !v)}
          />
          <ErrorMessageLabel name="price" />
        </FormGroup>
        <FormGroup>
          <FormLabel>Cena Odštete (RSD)</FormLabel>
          <InputWithSuffix
            name="penalty_price"
            type="number"
            step={0.1}
            autoComplete="off"
            disabled={isPenaltyLocked}
            suffix={isPenaltyLocked ? <MdLock /> : <MdLockOpen />}
            onSuffixClick={() => setIsPenaltyLocked((v) => !v)}
          />
          <ErrorMessageLabel name="penalty_price" />
        </FormGroup>
        <FormGroup>
          <FormLabel>Bodovi</FormLabel>
          <InputField name="points" type="number" autoComplete="off" />
          <ErrorMessageLabel name="points" />
        </FormGroup>
      </Col>
    </Row>
  );
}

export default ProductInfo;
